/**
 * VocabularyScreen — "Your vocabulary" (VOCAB-105).
 *
 * The learner-facing mastery surface: aggregate New / Learning /
 * Mastered counts (vocab + expressions combined), the due-for-review
 * queue, and a paged Learning / Mastered browse list. First consumer of
 * the generated vocab API clients.
 *
 * Spec: docs/specs/mobile/vocabulary-screen.mobile.md
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';

import { ScreenHeader } from '../components/ui/ScreenHeader';
import { WebContainer } from '../components/ui/WebContainer';
import { StatusChip } from '../components/roleplay/StatusChip';
import { LevelProgressPanel, defaultLevel } from '../components/vocab/LevelProgressPanel';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useResolveBilingual, useResolveTerm } from '../hooks/useResolveBilingual';
import { errorMessage } from '../generated/api/endpoint-errors';
import { useUserPreferencesStore } from '../store/userPreferencesStore';
import { selectActiveCourseId } from '../store/userPreferencesSelectors';
import type { CEFRLevel, VocabItemSummary, VocabStatusCounts } from '../generated/api/http-contracts';
import {
  useVocabStore,
  type DueListItem,
  type VocabListKind,
  selectVocabSummary,
  selectVocabSummaryLoading,
  selectVocabSummaryError,
  selectVocabDueItems,
  selectVocabDueLoading,
  selectVocabListKind,
  selectVocabListItems,
  selectVocabListLoading,
  selectVocabListLoadingMore,
  selectVocabListError,
  selectLoadVocabSummary,
  selectLoadVocabDue,
  selectSetVocabListKind,
  selectLoadVocabList,
  selectLoadMoreVocabList,
} from '../store/vocabStore';

const MAX_DUE_ROWS = 20;

function combined(counts: { vocab: VocabStatusCounts; expressions: VocabStatusCounts } | null) {
  if (!counts) return null;
  return {
    new: counts.vocab.new + counts.expressions.new,
    learning: counts.vocab.learning + counts.expressions.learning,
    mastered: counts.vocab.mastered + counts.expressions.mastered,
    due: counts.vocab.due + counts.expressions.due,
  };
}

// ── Count tile ────────────────────────────────────────────────────

const CountTile = React.memo(function CountTile({
  label,
  value,
  emphasis,
  testID,
}: {
  label: string;
  value: number | null;
  emphasis?: boolean;
  testID: string;
}) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  return (
    <View
      testID={testID}
      style={[styles.tile, { backgroundColor: theme.card, borderColor: theme.border }]}
    >
      <Text
        allowFontScaling={false}
        style={{
          fontSize: 22 * fontScale,
          fontWeight: '700',
          color: emphasis ? theme.primary : theme.text,
          ...ffs,
        }}
      >
        {value ?? '—'}
      </Text>
      <Text
        allowFontScaling={false}
        style={{ fontSize: 12 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
      >
        {label}
      </Text>
    </View>
  );
});

// ── Term row (shared by due queue + browse list) ──────────────────

const TermRow = React.memo(function TermRow({
  term,
  translation,
  status,
  testID,
}: {
  term: string;
  translation: Record<string, string>;
  status: 'new' | 'learning' | 'mastered';
  testID: string;
}) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const resolve = useResolveBilingual();
  // The headword is the American form; `translation.en_GB` carries the British
  // one. Print the variant the learner actually selected, not the catalog key.
  const resolveTerm = useResolveTerm();
  return (
    <View testID={testID} style={[styles.row, { backgroundColor: theme.card, borderColor: theme.border }]}>
      <View style={{ flex: 1, flexShrink: 1 }}>
        <Text
          allowFontScaling={false}
          numberOfLines={1}
          style={{ fontSize: 16 * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {resolveTerm(term, translation)}
        </Text>
        <Text
          allowFontScaling={false}
          numberOfLines={1}
          style={{ fontSize: 13 * fontScale, color: theme.textSecondary, marginTop: 2, ...ffs }}
        >
          {resolve(translation)}
        </Text>
      </View>
      <StatusChip status={status} />
    </View>
  );
});

// ── Screen ────────────────────────────────────────────────────────

export function VocabularyScreen() {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();
  const navigation = useNavigation();
  const activeCourseId = useUserPreferencesStore(selectActiveCourseId);

  const summary = useVocabStore(selectVocabSummary);
  const summaryLoading = useVocabStore(selectVocabSummaryLoading);
  const summaryError = useVocabStore(selectVocabSummaryError);
  const dueItems = useVocabStore(selectVocabDueItems);
  const dueLoading = useVocabStore(selectVocabDueLoading);
  const listKind = useVocabStore(selectVocabListKind);
  const listItems = useVocabStore(selectVocabListItems);
  const listLoading = useVocabStore(selectVocabListLoading);
  const listLoadingMore = useVocabStore(selectVocabListLoadingMore);
  const listError = useVocabStore(selectVocabListError);
  const loadSummary = useVocabStore(selectLoadVocabSummary);
  const loadDue = useVocabStore(selectLoadVocabDue);
  const setListKind = useVocabStore(selectSetVocabListKind);
  const loadList = useVocabStore(selectLoadVocabList);
  const loadMoreList = useVocabStore(selectLoadMoreVocabList);

  // The band the learner has explicitly tapped, or null to follow their
  // assessed level. Two things fall out of storing the course alongside the
  // pick: the panel lands on the assessed band the moment the summary arrives
  // (no render on the wrong level first), and a pick made under another course
  // simply stops applying (VS-006) — no reset effect, so no cascading render.
  const [pick, setPick] = useState<{ courseId: string | null; level: CEFRLevel } | null>(null);
  const selectedLevel =
    pick && pick.courseId === activeCourseId ? pick.level : defaultLevel(summary?.assessedLevel);
  const handleSelectLevel = useCallback(
    (level: CEFRLevel) => setPick({ courseId: activeCourseId, level }),
    [activeCourseId],
  );

  // Refresh on focus (VS-005) — a role play just played can change counts.
  useFocusEffect(
    useCallback(() => {
      if (!activeCourseId) return;
      void loadSummary(activeCourseId);
      void loadDue(activeCourseId);
      void loadList(activeCourseId);
    }, [activeCourseId, loadSummary, loadDue, loadList]),
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);
  const handleRetry = useCallback(() => {
    if (!activeCourseId) return;
    void loadSummary(activeCourseId);
    void loadDue(activeCourseId);
    void loadList(activeCourseId);
  }, [activeCourseId, loadSummary, loadDue, loadList]);
  const handleKindLearning = useCallback(() => {
    if (activeCourseId) setListKind(activeCourseId, 'learning');
  }, [activeCourseId, setListKind]);
  const handleKindMastered = useCallback(() => {
    if (activeCourseId) setListKind(activeCourseId, 'mastered');
  }, [activeCourseId, setListKind]);
  const handleEndReached = useCallback(() => {
    if (activeCourseId) void loadMoreList(activeCourseId);
  }, [activeCourseId, loadMoreList]);

  const totals = useMemo(() => combined(summary), [summary]);
  const dueShown = useMemo(() => dueItems.slice(0, MAX_DUE_ROWS), [dueItems]);
  // Overflow counts against the TRUE due total (the summary's exact
  // server-side count), not the fetched-and-capped `dueItems` length:
  // `loadDue` fetches at most DUE_FETCH_LIMIT vocab + DUE_FETCH_LIMIT
  // expression rows (≤100), so `dueItems.length` would silently cap the
  // "+N more" line far below the real backlog — e.g. read 80 (100 fetched −
  // 20 shown) when 150 are actually due and the badge/tile say 150 (correct
  // is 130). Fall back to the fetched length only until the summary lands.
  const dueOverflow =
    totals != null
      ? Math.max(0, totals.due - dueShown.length)
      : dueItems.length - dueShown.length;

  const renderItem = useCallback(
    ({ item }: { item: VocabItemSummary }) => (
      <TermRow
        term={item.lemma}
        translation={item.translation}
        status={item.status}
        testID={`vocabulary-list-item-${item.id}`}
      />
    ),
    [],
  );
  const keyExtractor = useCallback((item: VocabItemSummary) => item.id, []);

  const segment = useCallback(
    (kind: VocabListKind, label: string, onPress: () => void) => {
      const active = listKind === kind;
      return (
        <TouchableOpacity
          testID={`vocabulary-segment-${kind}`}
          accessibilityRole="button"
          accessibilityLabel={label}
          accessibilityState={{ selected: active }}
          onPress={onPress}
          style={[
            styles.segment,
            {
              backgroundColor: active ? theme.primary : theme.card,
              borderColor: active ? theme.primary : theme.border,
            },
          ]}
        >
          <Text
            allowFontScaling={false}
            style={{
              fontSize: 13 * fontScale,
              fontWeight: '600',
              color: active ? '#fff' : theme.text,
              ...ffs,
            }}
          >
            {label}
          </Text>
        </TouchableOpacity>
      );
    },
    [listKind, theme, fontScale, ffs],
  );

  const listHeader = useMemo(
    () => (
      <View>
        {/* Counts */}
        <View style={styles.tilesRow}>
          <CountTile label={t('vocabulary.counts.new')} value={totals?.new ?? null} testID="vocabulary-count-new" />
          <CountTile
            label={t('vocabulary.counts.learning')}
            value={totals?.learning ?? null}
            testID="vocabulary-count-learning"
          />
          <CountTile
            label={t('vocabulary.counts.mastered')}
            value={totals?.mastered ?? null}
            emphasis
            testID="vocabulary-count-mastered"
          />
        </View>

        {/* Leveled progress (VS-101). Renders nothing when the server predates
            `byLevel` — see LevelProgressPanel rule 2. */}
        <LevelProgressPanel
          byLevel={summary?.byLevel}
          assessedLevel={summary?.assessedLevel}
          selectedLevel={selectedLevel}
          onSelectLevel={handleSelectLevel}
        />

        {/* Due for review */}
        <View style={styles.sectionHeader}>
          <Text
            allowFontScaling={false}
            accessibilityRole="header"
            style={{ fontSize: 18 * fontScale, fontWeight: '700', color: theme.text, ...ffs }}
          >
            {t('vocabulary.due.title')}
          </Text>
          {totals != null && totals.due > 0 && (
            <View style={[styles.dueBadge, { backgroundColor: theme.primary }]}>
              <Text
                allowFontScaling={false}
                style={{ fontSize: 12 * fontScale, color: '#fff', fontWeight: '700', ...ffs }}
              >
                {totals.due}
              </Text>
            </View>
          )}
        </View>
        {dueLoading && dueShown.length === 0 ? (
          <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 12 }} />
        ) : dueShown.length === 0 ? (
          <Text
            allowFontScaling={false}
            testID="vocabulary-due-empty"
            style={{ fontSize: 14 * fontScale, color: theme.textSecondary, marginBottom: 8, ...ffs }}
          >
            {t('vocabulary.due.empty')}
          </Text>
        ) : (
          <View style={{ gap: 8 }}>
            {dueShown.map((item: DueListItem) => (
              <TermRow
                key={`${item.kind}-${item.id}`}
                term={item.term}
                translation={item.translation}
                status={item.status}
                testID={`vocabulary-due-item-${item.id}`}
              />
            ))}
            {dueOverflow > 0 && (
              <Text
                allowFontScaling={false}
                testID="vocabulary-due-more"
                style={{ fontSize: 13 * fontScale, color: theme.textSecondary, ...ffs }}
              >
                {t('vocabulary.due.more', { overflow: dueOverflow })}
              </Text>
            )}
          </View>
        )}

        {/* Browse segments */}
        <View style={styles.segmentRow}>
          {segment('learning', t('vocabulary.list.learning'), handleKindLearning)}
          {segment('mastered', t('vocabulary.list.mastered'), handleKindMastered)}
        </View>
        {listError && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: 13 * fontScale, color: theme.error, marginBottom: 8, ...ffs }}
          >
            {errorMessage(listError)}
          </Text>
        )}
        {!listLoading && !listError && listItems.length === 0 && (
          <Text
            allowFontScaling={false}
            testID="vocabulary-list-empty"
            style={{ fontSize: 14 * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('vocabulary.list.empty')}
          </Text>
        )}
        {listLoading && listItems.length === 0 && (
          <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 12 }} />
        )}
      </View>
    ),
    [
      t,
      totals,
      summary,
      selectedLevel,
      handleSelectLevel,
      dueLoading,
      dueShown,
      dueOverflow,
      segment,
      handleKindLearning,
      handleKindMastered,
      listError,
      listLoading,
      listItems.length,
      theme,
      fontScale,
      ffs,
    ],
  );

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader title={t('vocabulary.title')} onBack={handleBack} />
      <WebContainer variant="page" style={{ flex: 1 }}>
        <View testID="vocabulary-screen" style={styles.content}>
          {summaryError ? (
            <View
              testID="vocabulary-error"
              style={[styles.errorBanner, { backgroundColor: theme.card, borderColor: theme.error }]}
            >
              <Text
                allowFontScaling={false}
                style={{ flex: 1, fontSize: 14 * fontScale, color: theme.text, ...ffs }}
              >
                {errorMessage(summaryError)}
              </Text>
              <TouchableOpacity
                testID="vocabulary-error-retry"
                accessibilityRole="button"
                accessibilityLabel={t('vocabulary.error.retry')}
                onPress={handleRetry}
                style={[styles.retryButton, { borderColor: theme.border }]}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: 14 * fontScale, color: theme.text, fontWeight: '600', ...ffs }}
                >
                  {t('vocabulary.error.retry')}
                </Text>
              </TouchableOpacity>
            </View>
          ) : summaryLoading && !summary ? (
            <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 24 }} />
          ) : (
            <FlatList
              testID="vocabulary-list"
              data={listItems}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              ListHeaderComponent={listHeader}
              ListFooterComponent={
                listLoadingMore ? (
                  <ActivityIndicator size="small" color={theme.primary} style={{ paddingVertical: 12 }} />
                ) : null
              }
              onEndReached={handleEndReached}
              onEndReachedThreshold={0.4}
              contentContainerStyle={{ gap: 8, paddingBottom: 24 }}
              accessibilityLabel={t('vocabulary.title')}
            />
          )}
        </View>
      </WebContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  content: { flex: 1, padding: 16 },
  tilesRow: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  tile: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  dueBadge: {
    minWidth: 22,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 11,
    alignItems: 'center',
  },
  segmentRow: { flexDirection: 'row', gap: 8, marginTop: 16, marginBottom: 8 },
  segment: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 16,
    borderWidth: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    gap: 8,
  },
  retryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
});
